import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Copy, Pause, Play } from 'lucide-react';
import { VoiceScrubber } from './VoiceScrubber';
import { SpeedChip } from './SpeedChip';
import { buildWaveform, formatVoiceTime, useVoicePlayback } from '../../hooks/useVoicePlayback';
interface VoiceMessageBubbleProps {
  text: string;
  time: string;
  isCaller: boolean;
  isLight: boolean;
  /** Only one message plays at a time — parent tracks which one is active. */
  isActive: boolean;
  onActivate: () => void;
  onDeactivate: () => void;
  /** Text direction of the transcript line (Arabic originals are RTL). */
  dir?: 'ltr' | 'rtl';
}
const LONG_PRESS_MS = 450;

/**
 * WhatsApp-style voice bubble: play/pause, a scrubbable waveform, elapsed time
 * and a 1× / 1.5× / 2× speed chip. The transcript stays readable underneath.
 * Long-press anywhere on the bubble opens a Copy action for the message text.
 */
export function VoiceMessageBubble({
  text,
  time,
  isCaller,
  isLight,
  isActive,
  onActivate,
  onDeactivate,
  dir = 'ltr'
}: VoiceMessageBubbleProps) {
  // Roughly 2.6 spoken words per second, never shorter than 2s.
  const duration = useMemo(() => Math.max(2, Math.round(text.split(/\s+/).length / 2.6)), [text]);
  const bars = useMemo(() => buildWaveform(text, 30), [text]);
  const player = useVoicePlayback({
    duration,
    isActive,
    onActivate,
    onDeactivate
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pressing, setPressing] = useState(false);
  const pressTimer = useRef<number | null>(null);
  useEffect(() => () => {
    if (pressTimer.current) window.clearTimeout(pressTimer.current);
  }, []);
  const cancelPress = () => {
    setPressing(false);
    if (pressTimer.current) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };
  const startPress = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('[data-voice-control]')) return;
    setPressing(true);
    pressTimer.current = window.setTimeout(() => {
      setPressing(false);
      setMenuOpen(true);
      navigator.vibrate?.(12);
    }, LONG_PRESS_MS);
  };
  const copyText = () => {
    navigator.clipboard?.writeText(text).catch(() => undefined);
    setCopied(true);
    window.setTimeout(() => {
      setMenuOpen(false);
      setCopied(false);
    }, 900);
  };
  const subtle = isCaller ? 'text-white/60' : isLight ? 'text-slate-400' : 'text-zinc-500';
  return <div className="relative" data-id="element-442" data-mp="c3869m11mzhe">
      <AnimatePresence data-id="element-443" data-mp="c49lm2p3e84x">
        {menuOpen && <motion.button type="button" aria-label="Close message menu" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} onClick={() => setMenuOpen(false)} className="fixed inset-0 z-40 cursor-default bg-black/10" data-id="element-444" data-mp="h1jig3et9m2s" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-445" data-mp="3fm5qocr436l8">
        {menuOpen && <motion.div initial={{
        opacity: 0,
        y: 4,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 4,
        scale: 0.96
      }} transition={{
        duration: 0.15,
        ease: [0.23, 1, 0.32, 1]
      }} className={`absolute bottom-full z-50 mb-1.5 ${isCaller ? 'right-0 origin-bottom-right' : 'left-0 origin-bottom-left'}`} data-id="element-446" data-mp="3fngv8esdside">
            <button type="button" onClick={copyText} className={`flex items-center gap-2 whitespace-nowrap rounded-xl px-3 py-2 text-[12px] font-semibold shadow-lg ${isLight ? 'bg-white text-slate-800 ring-1 ring-slate-200 shadow-slate-900/10' : 'bg-zinc-800 text-white ring-1 ring-white/10 shadow-black/40'}`} data-id="element-447" data-mp="2euareaeo2lb">
              {copied ? <Check className="h-3.5 w-3.5 text-vexu" data-id="element-448" data-mp="l7eyyif5tjis" /> : <Copy className="h-3.5 w-3.5" data-id="element-449" data-mp="156x2e379nun2" />}
              {copied ? 'Copied' : 'Copy message'}
            </button>
          </motion.div>}
      </AnimatePresence>

      <motion.div onPointerDown={startPress} onPointerUp={cancelPress} onPointerLeave={cancelPress} onPointerCancel={cancelPress} onContextMenu={e => e.preventDefault()} animate={{
      scale: pressing || menuOpen ? 0.97 : 1
    }} transition={{
      duration: 0.16,
      ease: [0.23, 1, 0.32, 1]
    }} className={`relative select-none rounded-[14px] px-3 py-2 ${menuOpen ? 'z-50' : ''} ${isCaller ? 'bg-vexu text-white rounded-tr-[4px]' : isLight ? 'bg-white text-slate-800 rounded-tl-[4px] shadow-[0_1px_2px_rgba(0,0,0,0.04)] border border-slate-100/80' : 'bg-[#1A1A1A] text-zinc-100 rounded-tl-[4px] border border-white/[0.04]'}`} data-id="element-450" data-mp="1e11ok9bgghw9">
        <div className="flex items-start gap-2" data-id="element-451" data-mp="2bn90y5e03ej6">
          <button type="button" data-voice-control onClick={player.togglePlay} aria-label={player.playing ? 'Pause voice message' : 'Play voice message'} className={`mt-[1px] grid h-[22px] w-[22px] flex-shrink-0 place-items-center rounded-full transition-colors ${player.showPlayer ? isCaller ? 'bg-white text-vexu' : 'bg-vexu text-white' : isCaller ? 'bg-white/15 text-white' : isLight ? 'bg-slate-50 text-slate-500 border border-slate-100' : 'bg-white/[0.06] text-zinc-400'}`} data-id="element-452" data-mp="2bpvb8csa0853">
            {player.playing ? <Pause className="h-2.5 w-2.5 fill-current" data-id="element-453" data-mp="38il8bdmbmkta" /> : <Play className="ml-[1px] h-2.5 w-2.5 fill-current" data-id="element-454" data-mp="33he0jek6hlmm" />}
          </button>

          <div className="min-w-0 flex-1" data-id="element-455" data-mp="2yjvohfav6qae">
            <AnimatePresence initial={false} data-id="element-456" data-mp="11qaqaqe7a92l">
              {player.showPlayer && <motion.div data-voice-control initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.18,
              ease: [0.23, 1, 0.32, 1]
            }} className="overflow-hidden" data-id="element-457" data-mp="wqxrd7a21bjn">
                  <div className="flex items-center gap-2 pb-1.5 pt-[3px]" data-id="element-458" data-mp="2h6nh8z9bhtz7">
                    <VoiceScrubber bars={bars} progress={player.progress} duration={duration} elapsed={player.elapsed} setProgress={player.setProgress} draggingRef={player.draggingRef} fillClass={isCaller ? 'bg-white' : 'bg-vexu'} trackClass={isCaller ? 'bg-white/35' : isLight ? 'bg-slate-300' : 'bg-white/20'} thumbClass={isCaller ? 'bg-white' : 'bg-vexu'} data-id="element-459" data-mp="2m8l566snxcyh" />
                    <SpeedChip speed={player.speed} onClick={player.cycleSpeed} surface={isCaller ? 'accent' : isLight ? 'light' : 'dark'} data-id="element-460" data-mp="1m1g63st08mm5" />
                  </div>
                </motion.div>}
            </AnimatePresence>

            <AnimatePresence mode="wait" initial={false} data-id="element-461" data-mp="138vnr5axlzwl">
              <motion.p key={text} dir={dir} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.14
            }} className={`text-[12px] leading-[1.45] ${dir === 'rtl' ? 'text-right text-[13px]' : 'tracking-[-0.01em]'}`} data-id="element-462" data-mp="139f0m7lq5y3r">
                {text}
              </motion.p>
            </AnimatePresence>
          </div>
        </div>

        <div className={`mt-0.5 flex items-center justify-between gap-2 text-[9px] tracking-wide ${subtle}`} data-id="element-463" data-mp="fjathq9bvg80">
          <span className="tabular-nums" data-id="element-464" data-mp="3v4p6t1yj6981">
            {player.showPlayer ? `${formatVoiceTime(player.elapsed)} / ${formatVoiceTime(duration)}` : formatVoiceTime(duration)}
          </span>
          <span data-id="element-465" data-mp="80iqff1zqur7">{time}</span>
        </div>
      </motion.div>
    </div>;
}